import { toDayjs } from '../format/dates';

import { bagOf } from './bag';
import { suggestDates, type BookingDraft, type DraftProblem } from './draft';
import { maxHoldHours } from './stageMoves';

/**
 * The four steps of taking a booking, on the website and the phone alike, over the shared booking
 * draft (`@core/bookings/draft`): **when, then who, then what, then the bag** (owner, 2026-11-05,
 * canvas "Booking flow: customer after dates", replacing when, what, who).
 *
 * <p>
 * The customer comes straight after the dates, so the looks and the bag read their real name. Someone
 * only asking what is free can still skip the question: the bag then asks, and nothing is confirmed
 * without a customer. Three things are required — a day, a garment, a customer — and everything else
 * starts from a default with Change beside it.
 * </p>
 */
export const newBookingSteps = ['when', 'customer', 'looks', 'bag'] as const;

export type NewBookingStep = (typeof newBookingSteps)[number];

export const stepTitle: Record<NewBookingStep, string> = {
  when: 'When is the event?',
  customer: 'Who is the customer?',
  looks: 'Who wears what',
  bag: 'Bag and confirm',
};

/**
 * Where a problem is fixed. **Exhaustive over the draft's steps**, so a problem kind added to the
 * shared rules is a compile error here until it has a screen. The draft's customer problems split by
 * their `part`: the dates are the first screen, the payer the second.
 */
export function stepFor(problem: DraftProblem): NewBookingStep {
  switch (problem.step) {
    case 'customer':
      return problem.part === 'payer' ? 'customer' : 'when';
    case 'party':
      return 'looks';
    case 'deposit':
    case 'discount':
    case 'stage':
      return 'bag';
    default: {
      const unhandled: never = problem.step;

      return unhandled;
    }
  }
}

/** The problems a step can fix. */
export function problemsFor(
  step: NewBookingStep,
  problems: readonly DraftProblem[],
): DraftProblem[] {
  return problems.filter((problem) => stepFor(problem) === step);
}

export function nextStep(step: NewBookingStep): NewBookingStep | null {
  return newBookingSteps[newBookingSteps.indexOf(step) + 1] ?? null;
}

export function previousStep(step: NewBookingStep): NewBookingStep | null {
  return newBookingSteps[newBookingSteps.indexOf(step) - 1] ?? null;
}

/**
 * Where going on leads: past the customer question once it is answered — a booking started from the
 * customer's own page, or a clerk who came back to change the dates.
 */
export function stepAfter(step: NewBookingStep, draft: BookingDraft): NewBookingStep | null {
  const next = nextStep(step);

  return next === 'customer' && draft.customerId !== null ? nextStep(next) : next;
}

/** The dates step's button, which names where it leads. */
export function afterWhenWords(draft: BookingDraft): string {
  return stepAfter('when', draft) === 'customer' ? 'Choose the customer' : 'Pick the looks';
}

/**
 * The step to come back to after the customer question: the looks or the bag when the clerk stepped
 * back from one, and none on the way through. Choosing someone then returns there, never to the start.
 */
export function returnFromCustomer(from: NewBookingStep): NewBookingStep | null {
  return from === 'looks' || from === 'bag' ? from : null;
}

export type CustomerStepExit = { kind: 'keep' | 'back' | 'skip'; label: string };

/**
 * The way on from the customer step without choosing anybody new: carry on with who is chosen, back
 * to the step the clerk came from, or skip the question for someone only asking what is free.
 */
export function customerStepExit(
  draft: BookingDraft,
  returnTo: NewBookingStep | null,
): CustomerStepExit {
  if (draft.customerId !== null) {
    return { kind: 'keep', label: `Carry on with ${draft.customerName}` };
  }

  return returnTo
    ? { kind: 'back', label: `Back to the ${returnTo === 'bag' ? 'bag' : 'looks'}` }
    : { kind: 'skip', label: 'Only asking what is free? Skip for now' };
}

export interface StepCard {
  title: string;
  detail: string;
  /** Answered and behind the clerk, so it wears a tick. A skipped customer keeps its number. */
  done: boolean;
}

/** What a step's card across the top says: what has been chosen so far, or what it will ask. */
export function stepCard(
  each: NewBookingStep,
  current: NewBookingStep,
  draft: BookingDraft,
): StepCard {
  const passed = newBookingSteps.indexOf(each) < newBookingSteps.indexOf(current);

  switch (each) {
    case 'when': {
      const day = toDayjs(draft.eventDate);
      const kind = draft.eventKind.trim() || 'Event';
      const named = day ? `${kind} · ${day.format('ddd D MMM')}` : '';
      const from = toDayjs(draft.possessionFrom);
      const to = toDayjs(draft.possessionTo);

      return passed && named && from && to
        ? {
            title: named,
            detail: `Out ${from.format('ddd D')} – ${to.format('ddd D')} · Change`,
            done: true,
          }
        : { title: 'When', detail: named || 'Pick the day of the event', done: passed };
    }
    case 'customer': {
      if (draft.customerId === null) {
        return {
          title: 'Customer',
          detail: passed ? 'Not chosen yet' : 'Find or add them',
          done: false,
        };
      }

      const change = passed ? ' · Change' : '';

      return {
        title: draft.customerName,
        detail: draft.customerMobile ? `${draft.customerMobile}${change}` : passed ? 'Change' : '',
        done: passed,
      };
    }
    case 'looks': {
      const bag = bagOf(draft);

      return {
        title: 'Who wears what',
        detail: bag.garments === 0 ? 'No garments yet' : bag.headline,
        done: passed,
      };
    }
    case 'bag':
      return {
        title: 'Bag and confirm',
        detail:
          current === 'bag' && draft.customerName ? `${draft.customerName} pays` : 'Money, confirm',
        done: false,
      };
    default: {
      const unhandled: never = each;

      return unhandled;
    }
  }
}

/** "1 of 4". */
export function stepCount(step: NewBookingStep): string {
  return `${newBookingSteps.indexOf(step) + 1} of ${newBookingSteps.length}`;
}

/** "Wedding on Wednesday 18 November", or "Wedding, Wed 18 – Fri 20 November" over several days. */
export function eventTitle(draft: BookingDraft): string {
  const kind = draft.eventKind.trim() || 'The event';
  const from = toDayjs(draft.chargeFrom);
  const to = toDayjs(draft.chargeTo);

  if (!from || !to || draft.chargeFrom === draft.chargeTo) {
    return `${kind} on ${toDayjs(draft.eventDate)?.format('dddd D MMMM') ?? ''}`;
  }

  return `${kind}, ${from.format('ddd D')} – ${to.format('ddd D MMMM')}`;
}

/** "Sat 24 Oct · out 22 to 26 Oct" — the dates read back above the looks and the bag. */
export function datesLine(draft: BookingDraft): string {
  const event = toDayjs(draft.eventDate);
  const from = toDayjs(draft.possessionFrom);
  const to = toDayjs(draft.possessionTo);

  return event && from && to
    ? `${event.format('ddd D MMM')} · out ${from.format('D')} to ${to.format('D MMM')}`
    : '';
}

/** The hold lengths offered instead of confirming — a day is the default, a week the server's ceiling. */
export const holdChoices = [4, 12, 24, 48, 72, maxHoldHours];

/** "12 hours", "1 day", "7 days". */
export function hoursWord(hours: number): string {
  return hours % 24 === 0 ? `${hours / 24} ${hours === 24 ? 'day' : 'days'}` : `${hours} hours`;
}

/**
 * The event over this many days from its first, the grace either side kept — the "1 day · 2 days ·
 * 3 days" choice beside the month. Nothing until a day is chosen.
 */
export function withEventDays(draft: BookingDraft, days: number): Partial<BookingDraft> {
  const from = toDayjs(draft.eventDate);

  if (!from || days < 1) {
    return {};
  }

  return suggestDates(draft.eventDate, {
    eventTo: from.add(days - 1, 'day').format('YYYY-MM-DD'),
    collectBeforeDays: draft.collectBeforeDays,
    returnAfterDays: draft.returnAfterDays,
  });
}
